import { useMemo } from 'react';

import { useCoverageRules } from '@/shared/hooks';
import { useDebounce } from '@/utils/useDebounce';

const formatDateForApi = (date?: Date) => {
  if (!date) {
    return undefined;
  }

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return undefined;
  }

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type UseDynamicCoverageRulesProps = {
  propertyId?: string;
  leaseStart?: Date;
  leaseEnd?: Date;
  grossMonthlyRent?: number;
  prepaidRent?: number;
  freeRent?: number;
  enabled?: boolean;
  /**
   * When true, the fetch is blocked until lease_start/end + gross_monthly_rent
   * are all populated. SMB needs this because its options are derived from
   * `rule.pricing` — fetching without pricing yields no usable options.
   * Institutional fetches eagerly on propertyId so customized labels appear
   * as soon as the property is selected.
   */
  waitForPricingParams?: boolean;
};

/**
 * Shared fetcher for DDS dynamic coverage rules.
 * - Debounces the five lease params (600ms) to avoid request storms on typing.
 * - Only sends pricing params when all three required fields are populated.
 * Both SMB and Institutional flows consume this hook for the fetching contract;
 * they diverge in how they render the resulting rules.
 */
export const useDynamicCoverageRules = ({
  propertyId,
  leaseStart,
  leaseEnd,
  grossMonthlyRent,
  prepaidRent,
  freeRent,
  enabled = true,
  waitForPricingParams = false,
}: UseDynamicCoverageRulesProps) => {
  const debouncedLeaseStartDate = useDebounce(formatDateForApi(leaseStart), 600);
  const debouncedLeaseEndDate = useDebounce(formatDateForApi(leaseEnd), 600);
  const debouncedGrossMonthlyRent = useDebounce(grossMonthlyRent, 600);
  const debouncedPrepaidRent = useDebounce(prepaidRent, 600);
  const debouncedFreeRent = useDebounce(freeRent, 600);

  const pricingParams = useMemo(
    () =>
      debouncedLeaseStartDate && debouncedLeaseEndDate && debouncedGrossMonthlyRent
        ? {
            lease_start_date: debouncedLeaseStartDate,
            lease_end_date: debouncedLeaseEndDate,
            gross_monthly_rent: debouncedGrossMonthlyRent,
            prepaid_rent: debouncedPrepaidRent,
            free_rent: debouncedFreeRent,
          }
        : undefined,
    [
      debouncedFreeRent,
      debouncedGrossMonthlyRent,
      debouncedLeaseEndDate,
      debouncedLeaseStartDate,
      debouncedPrepaidRent,
    ],
  );

  return useCoverageRules(propertyId ?? '', Boolean(enabled && propertyId), {
    params: pricingParams,
    waitForRequiredParams: waitForPricingParams,
  });
};
